import { Fragment, useState, useMemo, useEffect } from 'react';
import _ from 'lodash';
//services
//import { validateStep2 } from 'services/pay.services';
//reducers
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import {
	setPay,
	getNewPay,
	getMessage,
	setMessage,
	setActiveStep,
	getPayDetails,
	getPayPreference,
	getPayReason,
	getPayFees,
	isLoading,
} from 'state/reducers/pay.reducers';
//components
import { FormsStack } from 'theme/elements/Forms';
import { invalidFields, validationTypes } from 'components/FormCreator/types';
import { setListOfInvalids, validateFields } from 'components/FormCreator/validation';
import WarningDialog from 'components/WarningDialog';
//sections
import Footer from '../Footer';
//constants
import {
	Pay,
	paySteps,
	fieldsValidations,
	PayEnum,
	PaymentCreateProps,
	PaymentFees,
	paymentsTypes,
	swiftTypes,
	FileType,
	MAX_INVOICE_FILES,
} from 'constants/pay.constants';
import { userMessage, userMesaageType, timing } from 'constants/ui.constants';
import PaymentDetailsForm from './PaymentDetailsForm';
import { getCurrenciesObj } from 'state/reducers/global.reducers';
import { getCompanyBalances } from 'state/reducers/balance.reducers';
import PaymentPreferencesForm from './PaymentPreferencesForm';
import PaymentReasonForm from './PaymentReasonForm';
import { User } from 'constants/user.constants';
import { getUserDetails } from 'state/reducers/user.reducers';
import { validatePayment, uploadPaymentFile } from 'services/pay.services';
import { getPaymentFee } from '../../helper';
import { format } from 'date-fns';
import { maxFileSize } from 'constants/global.constants';
import { getFormattedNumber } from 'utils';

export const prepareObjectToValidate = (pay: Pay, user: User, agree: boolean) => {
	const obj = new FormData();
	PaymentCreateProps.forEach((key) => {
		switch (key) {
			case PayEnum.companyId:
				obj.append(key, String(user?.companyId));
				break;
			case PayEnum.paymentType:
				obj.append(key, _.upperFirst(String(_.get(pay, PayEnum.paymentType))));
				break;
			case PayEnum.paymentDate:
				obj.append(key, format(new Date(_.get(pay, PayEnum.paymentDate) || ''), timing.DATE_FOR_SERVICE));
				break;
			case PayEnum.invoiceFile:
				let file: FileType[] | undefined = _.get(pay, PayEnum.invoiceFile);
				if (file) {
					const fileIds = _.keys(_.keyBy(file, 'id')).map((id) => parseInt(id, 10));
					fileIds.forEach((id) => obj.append('fileIds', id.toString()));
				}
				break;
			case PayEnum.currencyId:
				obj.append(key, String(pay.beneficiary?.currencyId));
				break;
			case PayEnum.swift:
				if (_.isEqual(_.get(pay, PayEnum.paymentType), paymentsTypes.PRIORITY)) {
					obj.append(
						'isFeeShared',
						_.isEqual(_.get(pay, PayEnum.swift), swiftTypes.SHARE) ? 'true' : 'false'
					);
				}
				break;
			case PayEnum.paymentCategory:
				if (pay[PayEnum.paymentCategory]) {
					obj.append('paymentCategoryId', String(pay[PayEnum.paymentCategory]));
				}
				break;
			default:
				if (pay[key]) {
					obj.append(key, String(pay[key]));
				}
		}
	});
	if (agree) {
		obj.append('isSigned', 'true');
	}
	return obj;
};

export default function Step2(props: { handleCancel: any }) {
	const t = useAppSelector(selectTranslations);
	const pay: Pay = useAppSelector(getNewPay);
	const payDetails = useAppSelector(getPayDetails);
	const payPreference = useAppSelector(getPayPreference);
	const payReason = useAppSelector(getPayReason);
	const userMessage: userMessage = useAppSelector(getMessage);
	const user: User = useAppSelector(getUserDetails);
	const dispatch = useAppDispatch();
	const CurrenciesObj = useAppSelector(getCurrenciesObj);
	const companyBalancesList = useAppSelector(getCompanyBalances);
	const [invalidFields, setInvalidFields] = useState<invalidFields>({});
	const [isFileUploaded, setIsFileUploaded] = useState(false);
	const [fileUpload, setFileUpload] = useState(false);
	const payFees: PaymentFees = useAppSelector(getPayFees);
	const ifLoading = useAppSelector(isLoading);

	const openDialog = useMemo(() => {
		return userMessage.exist;
	}, [userMessage]);

	const currency = useMemo(() => {
		let obj;
		let BalancesObj = _.keyBy(companyBalancesList, 'currencyId');
		if (_.has(CurrenciesObj, pay?.beneficiary?.currencyId || '')) {
			const cur = _.get(CurrenciesObj, pay?.beneficiary?.currencyId || '');
			obj = { ...cur, amount: 0 };
		}
		if (_.has(BalancesObj, pay?.beneficiary?.currencyId || '')) {
			const cur = _.get(BalancesObj, pay?.beneficiary?.currencyId || '');
			obj = { ...obj, amount: Number(cur?.amount - pay[PayEnum.fee]) || '' };
		}
		return obj;
	}, [pay[PayEnum.beneficiary], pay[PayEnum.fee]]);

	const handleFileUpload = async (files: File[]) => {
		setFileUpload(true);
		try {
			const _file = _.get(pay, PayEnum.invoiceFile) || [];
			const names = _.keys(_.keyBy(_file, 'name'));
			let uploadedFiles: FileType[] = [..._file];

			const uploadPromises = files
				.filter((file) => file.size <= maxFileSize && !names.includes(file.name))
				.map((file) => dispatch(uploadPaymentFile(file, user.companyId)));

			const results = await Promise.all(uploadPromises);
			const newFiles = results.reduce((acc, res) => {
				if (res?.success && res?.file) {
					acc.push({
						id: res.file.id,
						key: res.file.key,
						originalFileName: res.file.originalFileName,
						path: res.file.path,
						company_id: res.file.companyId,
					});
				}
				return acc;
			}, []);

			if (newFiles.length > 0) {
				uploadedFiles = [...uploadedFiles, ...newFiles];
				dispatch(setPay({ name: PayEnum.invoiceFile, value: uploadedFiles }));
				setIsFileUploaded(true);
			} else {
				dispatch(
					setMessage({
						exist: true,
						type: userMesaageType.ERROR,
						text: 'servers.errors.codes.1304',
						subText: 'No files were successfully uploaded',
					})
				);
			}
		} catch (err) {
			setFileUpload(false);
		} finally {
			setFileUpload(false);
		}
	};

	const handleRemoveFile = (fileIndex: number) => {
		let _file = _.get(pay, PayEnum.invoiceFile) || [];
		if (_.isArray(_file)) {
			let files: FileType[] = [];
			_file.map((file, index) => {
				index !== fileIndex && files.push(file);
			});
			dispatch(setPay({ name: PayEnum.invoiceFile, value: files }));
			setIsFileUploaded(!!files.length);
		}
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;

		if (name === 'amount') {
			if (!(currency.decimalPlaces > 0)) {
				if (!fieldsValidations.amount?.includes(validationTypes.noDecimalAllowed)) {
					fieldsValidations.amount!.push(validationTypes.noDecimalAllowed);
				}
			} else {
				if (fieldsValidations.amount?.includes(validationTypes.noDecimalAllowed)) {
					fieldsValidations.amount = fieldsValidations.amount!.filter(
						(v) => v !== validationTypes.noDecimalAllowed
					);
				}
			}
		}
		dispatch(setPay({ name: name, value: value }));
		if (_.has(fieldsValidations, name)) {
			setInvalidFields(setListOfInvalids(invalidFields, fieldsValidations, name, value));
		}
	};

	const checkAmount = useMemo(() => {
		if (Number(payDetails.amount) < 0 || Number(currency.amount) < Number(payDetails.amount)) {
			setInvalidFields({
				...invalidFields,
				amount: {
					name: 'amount',
					valid: false,
					errorText:
						t('validations.errors.avaliable_amount') + ' ' + currency?.symbol + getFormattedNumber(currency?.amount),
				},
			});
			return false;
		}
		return true;
	}, [pay[PayEnum.amount], pay[PayEnum.fee], currency]);

	const isValidForm = useMemo(() => {
		//check fields
		let invalids =
			validateFields(fieldsValidations, {
				...payDetails,
				...payPreference,
				...payReason,
			}) || {};

		return invalids;
	}, [pay]);

	useEffect(() => {
		dispatch(setPay({ name: PayEnum.fee, value: getPaymentFee(pay, payFees) }));
	}, [pay.paymentType, pay.swift, payFees]);

	useEffect(() => {
		if (!pay.paymentType) {
			if (pay.beneficiary?.isLocalPaymentAllowed) {
				dispatch(setPay({ name: PayEnum.paymentType, value: paymentsTypes.LOCAL }));
			} else if (pay.beneficiary?.isPriorityPaymentAllowed) {
				dispatch(setPay({ name: PayEnum.paymentType, value: paymentsTypes.PRIORITY }));
			}
		}
	}, [pay]);

	const paymentFee = useMemo(() => {
		return _.get(CurrenciesObj, payFees.currencyId || '')?.symbol + pay[PayEnum.fee];
	}, [payFees, pay[PayEnum.fee]]);

	const validateStep = () => {
		dispatch(setMessage({ ...userMessage, exist: false }));
		const invalidFields = isValidForm;
		if (!_.keys(invalidFields).length && checkAmount) {
			dispatch(validatePayment(prepareObjectToValidate(pay, user, false)));
		} else {
			dispatch(
				setMessage({
					exist: true,
					type: userMesaageType.ERROR,
					text: 'missing payment',
				})
			);
			setInvalidFields(invalidFields);
		}
	};
	return (
		<Fragment>
			<WarningDialog
				open={openDialog}
				title={t(userMessage.text || '')}
				subTitle={userMessage.subText}
				handleCancel={() => {
					dispatch(setMessage({ exist: false }));
				}}
			/>
			<FormsStack>
				<PaymentDetailsForm currency={currency} invalidFields={invalidFields} handleChange={handleChange} />
				<PaymentPreferencesForm
					currency={currency}
					invalidFields={invalidFields}
					paymentFee={paymentFee}
					handleChange={handleChange}
				/>
				<PaymentReasonForm
					handleTransferProofFile={handleFileUpload}
					isFileUploaded={isFileUploaded}
					fileUpload={fileUpload}
					currency={currency}
					invalidFields={invalidFields}
					handleChange={handleChange}
					removeFile={handleRemoveFile}
					//handleRemoveFile={handleRemoveFile}
				/>
				<Footer
					handleCancel={props.handleCancel}
					handleBack={() => {
						dispatch(setActiveStep(paySteps.BENEFICIARY_DETAILS));
					}}
					ifBack={true}
					disableBack={ifLoading}
					disableCancel={ifLoading}
					disableSubmit={_.keys(isValidForm).length === 0 && checkAmount && !ifLoading ? false : true}
					labelSubmit={t('page.pay.create.step.2.review_button.text')}
					labelCancel={t('page.pay.create.step.2.cancel_button.text')}
					labelBack={t('page.pay.create.step.2.back_button.text')}
					handleSubmit={validateStep}
				/>
			</FormsStack>
		</Fragment>
	);
}
